import React, { useMemo } from 'react'

import {
  Button,
  ButtonClean,
  ConfirmModal,
  Loader,
  Subsection,
  useModalContext,
} from '@fourthwall/components'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import { routing } from '@utils/routing'

import { useSubscription } from '../../../../../../modules/Subscriptions'
import * as S from './CustomDomain.styled'
import type { DomainStatus } from './CustomDomain.types'
import { DomainTile } from './components/DomainTile/DomainTile'
import { useCustomDomain } from './hooks/useCustomDomain'

export const CustomDomain = () => {
  const { currentShop } = useCurrentShop()
  const {
    configurationStatus,
    connectionStatus,
    domainName,
    paidDomainName,
    isDnsConfigured,
    isConnecting,
    isDeleting,
    isRetrying,
    connectPaidDomain,
    deleteCustomDomain,
    retryCustomDomainConnection,
  } = useCustomDomain()
  const { open } = useModalContext()
  const { hasActiveProSubscription } = useSubscription()

  const paidDomainStatus: DomainStatus = useMemo(() => {
    if (configurationStatus !== 'PAID_DOMAIN_ONLY') {
      return 'NOT_CONNECTED'
    }

    if (isDnsConfigured && connectionStatus === 'NOT_CONNECTED') {
      return 'CONNECTING'
    }

    return connectionStatus
  }, [configurationStatus, connectionStatus, isDnsConfigured])

  const handleDomainDelete = () => {
    open(ConfirmModal, {
      title: 'Are you sure you want to delete this domain?',
      text: (
        <S.RemoveDomainTextContainer>
          You&apos;ll still be able to access {currentShop?.internalDomain}.fourthwall.com
        </S.RemoveDomainTextContainer>
      ),
      confirmLabel: 'Delete',
      confirmAppearance: 'destructive',
      onConfirm: deleteCustomDomain,
    })
  }

  const renderContent = () => {
    if (configurationStatus === 'LOADING') {
      return (
        <S.LoaderContainer>
          <Loader size="large" />
        </S.LoaderContainer>
      )
    }

    if (configurationStatus === 'NONE') {
      return (
        <S.ButtonContainer>
          <Button appearance="primary" label="Connect domain" to={routing.settings.domain.create} />
        </S.ButtonContainer>
      )
    }

    return (
      <>
        {(configurationStatus === 'CUSTOM_DOMAIN_ONLY' || configurationStatus === 'BOTH') && (
          <DomainTile
            domainName={domainName}
            status={connectionStatus}
            showViewDnsButton={
              connectionStatus === 'CONNECTED' ||
              connectionStatus === 'PARTIALLY_CONNECTED' ||
              connectionStatus === 'SYNCING_YOUR_DOMAIN'
            }
            isRetrying={isRetrying}
            isConnecting={isConnecting}
            onDomainDelete={
              !isDeleting && connectionStatus !== 'REMOVAL_IN_PROGRESS'
                ? handleDomainDelete
                : undefined
            }
            onRetryConnection={retryCustomDomainConnection}
          />
        )}
        {(configurationStatus === 'PAID_DOMAIN_ONLY' || configurationStatus === 'BOTH') && (
          <DomainTile
            domainName={paidDomainName}
            status={paidDomainStatus}
            showSubscriptionBadge={hasActiveProSubscription}
            isRetrying={isRetrying}
            isConnecting={isConnecting}
            removeDomainLabel="Disconnect domain"
            onDomainConnect={connectPaidDomain}
            onDomainDelete={
              configurationStatus === 'PAID_DOMAIN_ONLY' &&
              paidDomainStatus !== 'REMOVAL_IN_PROGRESS' &&
              paidDomainStatus !== 'NOT_CONNECTED' &&
              !isDeleting
                ? handleDomainDelete
                : undefined
            }
          />
        )}
        {configurationStatus === 'PAID_DOMAIN_ONLY' && (
          <ButtonClean
            label="Connect a custom domain"
            appearance="secondary"
            to={routing.settings.domain.create}
          />
        )}
      </>
    )
  }

  return (
    <Subsection title="Custom domain">
      <S.Container>
        <p>
          Set a custom domain name for your site (i.e. mywebsite.com).{' '}
          <ButtonClean
            label="Learn more"
            href="https://help.fourthwall.com/hc/en-us/articles/13331283794587-How-do-I-use-a-custom-domain"
            target="_blank"
          />
        </p>
        {renderContent()}
      </S.Container>
    </Subsection>
  )
}
